import { put, takeLatest, select, delay } from "redux-saga/effects"
import { toast } from "react-toastify"
import { MODAL_MESSAGES } from "constants/message"
import { RESPONSE_MODAL_TYPES } from "constants/modal-types"

import history from "app/history"
import { showResponseModal, closeResponseModal } from "store/user/actions"
import {
  FETCH_COURSE_BY_ID_FAILED,
  FETCH_BUNDLE_BY_ID_FAILED,
  UPLOAD_SNAPSHOT_SUCCESSFUL,
  UPLOAD_SNAPSHOT_FAILED,
  VERIFY_USER_SELFIE_SUCCESSFUL,
  VERIFY_USER_SELFIE_FAILED,
  POST_PAYMENT_SUCCESSFUL,
  POST_PAYMENT_FAILED,
  FETCH_TRANSACTION_DETAILS_FAILED,
} from "./action-types"

import {
  uploadSnapshot,
  setSnapshotError,
  setVerificationError,
} from "./action"

function* watchFetchCourseByIdFailed() {
  yield takeLatest(
    FETCH_COURSE_BY_ID_FAILED,
    function* handleFetchCourseByIdFailed({ error }) {
      yield history.push("/courses")
      toast.error("Requested course isn't available")
    }
  )
}

function* watchFetchBundleByIdFailed() {
  yield takeLatest(
    FETCH_BUNDLE_BY_ID_FAILED,
    function* handleFetchBundleByIdFailed({ error }) {
      yield history.push("/course-bundles")
      toast.error("Requested course bundle isn't available")
    }
  )
}

function* watchUploadSnapshotSuccess() {
  yield takeLatest(
    UPLOAD_SNAPSHOT_SUCCESSFUL,
    function* handleUploadSnapshotSuccess({ meta }) {
      const {
        previousAction: { payload = {} },
      } = meta
      const { request = {} } = payload

      const userId = yield select((state) => state.authReducer.userId)
      const courseId = request?.data?.course_id

      const retryCountKey = `ssFailRetry-${userId}-${courseId}`

      localStorage.setItem(retryCountKey, 0)
    }
  )
}

function* watchUploadSnapshotFailed() {
  yield takeLatest(
    UPLOAD_SNAPSHOT_FAILED,
    function* handleUploadSnapshotFailed({ error, meta }) {
      const {
        response: { data, status },
      } = error
      const {
        previousAction: { payload = {} },
      } = meta
      const { request = {} } = payload

      const userId = yield select((state) => state.authReducer.userId)
      const courseId = request?.data?.course_id

      const retryCountKey = `ssFailRetry-${userId}-${courseId}`

      const retryCountValue = parseInt(localStorage.getItem(retryCountKey)) || 0

      if (status === 400) {
        if (data.message === "No face detected in the snapshot.") {
          yield put(setSnapshotError("faceDetection"))
          if (document.fullscreenElement) {
            document.exitFullscreen()
          }
        } else {
          if (retryCountValue < 5) {
            localStorage.setItem(retryCountKey, retryCountValue + 1)
            yield delay(1000)
            yield put(uploadSnapshot(request?.data))
          } else {
            yield put(setSnapshotError("retryLimit"))
          }
        }
      } else if (status) {
        yield put(setSnapshotError("serverError"))
      }
    }
  )
}

function* watchVerifySelfieSuccess() {
  yield takeLatest(
    VERIFY_USER_SELFIE_SUCCESSFUL,
    function* handleVerifySelfieSuccess({ payload, meta }) {
      const { data } = payload

      if (!data.success) {
        yield put(setVerificationError(data.message))
      } else {
        yield put(setVerificationError(false))
      }
    }
  )
}

function* watchVerifySelfieFailed() {
  yield takeLatest(
    VERIFY_USER_SELFIE_FAILED,
    function* handleVerifySelfieFailed({ error }) {
      const {
        response: { data },
      } = error

      if (data?.message) {
        yield put(setVerificationError(data.message))
      } else {
        yield put(setVerificationError("Failed to upload selfie."))
      }
    }
  )
}

function* watchPayCourseEnrollmentSuccess() {
  yield takeLatest(
    POST_PAYMENT_SUCCESSFUL,
    function* handlePaidCourseEnrollmentSuccess({ payload }) {
      const { data } = payload

      let modalData = {
        header: data.success,
        verified: true,
      }

      const courses = yield select((state) => state.coursesReducer)
      if (data?.data?.redirect_url || data?.data?.payment_url) {
        yield window.location.replace(
          data?.data?.redirect_url || data?.data?.payment_url
        )
      } else if (data?.txn_id && data?.data?.channel) {
        yield history.push(`/course/payment/${data?.txn_id}/instruction`)
      } else if (
        ["notpaid", "partial", "collection", "pending"].includes(
          data?.data?.status
        )
      ) {
        yield history.push(`/enrollment/payment?&txn_id=${data?.txn_id}`)
      } else if (["pay_later"].includes(data?.status)) {
        modalData.header = "Payment Pending"
        modalData.modalType = RESPONSE_MODAL_TYPES.WARNING
        modalData.message = MODAL_MESSAGES.PAYMENT_LATER_PENDING
        modalData.actionLabel = "VIEW COURSE"
        modalData.action = () =>
          history.push(`/course/${courses?.selectedCourse.slug}`)
        yield put(showResponseModal(modalData))
      } else {
        yield history.push(`/enrollment/payment?&txn_id=${data?.txn_id}`)
      }
    }
  )
}

function* watchPaidCourseEnrollmentFailed() {
  yield takeLatest(
    POST_PAYMENT_FAILED,
    function* handlePaidCourseEnrollmentFailed({ error }) {
      const { status, data } = error.response || {}

      let modalData = {
        modalType: RESPONSE_MODAL_TYPES.ERROR,
        header: "Payment Failed",
        message: MODAL_MESSAGES.PAYMENT_FAILED_CHANNEL,
        actionLabel: "CLOSE",
        action: () => closeResponseModal(),
      }

      if (status === 400) {
        if (data.detail === "User is already enrolled.") {
          modalData.header = data.detail
          modalData.message = MODAL_MESSAGES.ALREADY_ENROLLED
          modalData.actionLabel = "REFRESH PAGE"
          modalData.action = () => window.location.reload()
          modalData.verified = false
        }
      } else {
        modalData.header = "Payment Failed"
        modalData.message = MODAL_MESSAGES.PAYMENT_FAILED_CHANNEL
        modalData.actionLabel = "CLOSE"
        modalData.action = closeResponseModal()
        modalData.verified = false
      }

      yield put(showResponseModal(modalData))
    }
  )
}

function* watchFetchTransactionDetailsFailed() {
  yield takeLatest(
    FETCH_TRANSACTION_DETAILS_FAILED,
    function* handleFetchTransactionDetailsFailed() {
      toast.error("Invalid Payment Transaction ID")
      yield history.push("/")
    }
  )
}

export default [
  watchFetchCourseByIdFailed,
  watchFetchBundleByIdFailed,
  watchUploadSnapshotSuccess,
  watchUploadSnapshotFailed,
  watchVerifySelfieSuccess,
  watchVerifySelfieFailed,
  watchPayCourseEnrollmentSuccess,
  watchPaidCourseEnrollmentFailed,
  watchFetchTransactionDetailsFailed,
]
